import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, X, Loader2, Database } from "lucide-react";
import { useCreateEnterpriseSoftware, useUpdateEnterpriseSoftware } from "@/hooks/useAIInventory";
import { ToolMultiSelect, licenseTypeLabels, softwareCategories_options, getDataSourceTypeLabel } from "./shared";
import { toast } from "sonner";
import ImportFromProjectsDialog, { Candidate } from "./ImportFromProjectsDialog";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SoftwareTabProps {
  enterpriseSw: any[];
  dataSources: any[];
  businessAreas: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
  orgId: string | null;
  isSuperAdmin: boolean;
  selectedOrg: string;
  profileOrgId?: string;
  onDeleteConfirm: (item: { type: string; id: string; name: string }) => void;
}

interface SoftwareFormProps {
  data: any;
  setData: (d: any) => void;
  businessAreas: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
}

function SoftwareForm({ data, setData, businessAreas, aiToolCatalog }: SoftwareFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Nombre</Label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} placeholder="Ej: SAP S/4HANA" /></div>
        <div className="space-y-2">
          <Label>Categoría</Label>
          <Select value={data.category} onValueChange={(v) => setData({...data, category: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{softwareCategories_options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Proveedor</Label><Input value={data.vendor || ''} onChange={(e) => setData({...data, vendor: e.target.value})} /></div>
        <div className="space-y-2"><Label>Versión</Label><Input value={data.version || ''} onChange={(e) => setData({...data, version: e.target.value})} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Departamento</Label>
          <Select value={data.department || undefined} onValueChange={(v) => setData({...data, department: v})}>
            <SelectTrigger><SelectValue placeholder="Seleccionar departamento" /></SelectTrigger>
            <SelectContent>{businessAreas.map((a: any) => <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Tipo de Licencia</Label>
          <Select value={data.license_type || 'per_user'} onValueChange={(v) => setData({...data, license_type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="per_user">Por usuario</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
              <SelectItem value="freemium">Freemium</SelectItem>
              <SelectItem value="open_source">Open Source</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Usuarios</Label><Input type="number" value={data.users_count ?? ''} onChange={(e) => setData({...data, users_count: e.target.value === '' ? '' : Number(e.target.value)})} /></div>
        <div className="space-y-2"><Label>Costo Anual (USD)</Label><Input type="number" value={data.annual_cost ?? ''} onChange={(e) => setData({...data, annual_cost: e.target.value === '' ? '' : Number(e.target.value)})} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>IA Integrada</Label>
          <Select value={data.integration_with_ai ? "yes" : "no"} onValueChange={(v) => setData({...data, integration_with_ai: v === "yes"})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estado</Label>
          <Select value={data.status} onValueChange={(v) => setData({...data, status: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      {data.integration_with_ai && (
        <div className="space-y-2">
          <Label>Herramientas IA Conectadas</Label>
          <ToolMultiSelect selected={data.ai_tools_connected || []} onChange={(t) => setData({...data, ai_tools_connected: t})} catalogTools={aiToolCatalog} />
        </div>
      )}
    </div>
  );
}

export default function SoftwareTab({ enterpriseSw, dataSources, businessAreas, aiToolCatalog, orgId, isSuperAdmin, selectedOrg, profileOrgId, onDeleteConfirm }: SoftwareTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [linkedSourcesModal, setLinkedSourcesModal] = useState<{ swName: string; swId: string } | null>(null);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");

  const [form, setForm] = useState({ name: "", category: "ERP", vendor: "", version: "", department: "", users_count: 0, license_type: "per_user", annual_cost: 0, integration_with_ai: false, ai_tools_connected: [] as string[], status: "active" });

  const createSw = useCreateEnterpriseSoftware();
  const qc = useQueryClient();
  const updateSw = useUpdateEnterpriseSoftware();

  const categories = useMemo(() => [...new Set(enterpriseSw.map((s: any) => s.category).filter(Boolean))], [enterpriseSw]);
  const depts = useMemo(() => [...new Set(enterpriseSw.map((s: any) => s.department).filter(Boolean))], [enterpriseSw]);

  const filtered = useMemo(() => {
    return enterpriseSw.filter((sw: any) => {
      const matchesSearch = (sw.name || '').toLowerCase().includes(search.toLowerCase()) || (sw.vendor || '').toLowerCase().includes(search.toLowerCase()) || (sw.department || '').toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === "all" || sw.category === categoryFilter;
      const matchesStatus = statusFilter === "all" || sw.status === statusFilter;
      const matchesDept = deptFilter === "all" || sw.department === deptFilter;
      return matchesSearch && matchesCategory && matchesStatus && matchesDept;
    });
  }, [enterpriseSw, search, categoryFilter, statusFilter, deptFilter]);

  const getOrgIdForInsert = () => {
    if (isSuperAdmin) {
      if (selectedOrg === 'all') { toast.error('Selecciona una organización específica'); return null; }
      return selectedOrg;
    }
    return profileOrgId;
  };

  const handleAdd = () => {
    const oid = getOrgIdForInsert();
    if (!oid) return;
    createSw.mutate({ organization_id: oid, ...form }, {
      onSuccess: () => {
        setForm({ name: "", category: "ERP", vendor: "", version: "", department: "", users_count: 0, license_type: "per_user", annual_cost: 0, integration_with_ai: false, ai_tools_connected: [], status: "active" });
        setIsDialogOpen(false);
      }
    });
  };

  const handleEdit = () => {
    if (!editing) return;
    updateSw.mutate({
      id: editing.id, name: editing.name, category: editing.category, vendor: editing.vendor,
      version: editing.version, department: editing.department, users_count: editing.users_count,
      license_type: editing.license_type, annual_cost: editing.annual_cost,
      integration_with_ai: editing.integration_with_ai, status: editing.status,
      ai_tools_connected: editing.ai_tools_connected || [],
    }, { onSuccess: () => setEditing(null) });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle>Software Empresarial</CardTitle>
            <div className="flex gap-2">
              <ImportFromProjectsDialog
                type="software"
                orgId={orgId}
                existingNames={enterpriseSw.map((s: any) => s.name).filter(Boolean)}
                aiToolCatalog={aiToolCatalog}
                onImport={async (items: Candidate[]) => {
                  const oid = getOrgIdForInsert();
                  if (!oid) throw new Error("Selecciona organización");
                  const rows = items.map((c) => ({
                    organization_id: oid,
                    name: c.name,
                    category: c.meta?.category || null,
                    vendor: c.meta?.vendor || null,
                    integration_with_ai: false,
                    status: "active" as const,
                    source_projects: c.sourceProjects.length > 0 ? c.sourceProjects : null,
                  }));
                  const { error } = await supabase.from("enterprise_software").insert(rows as any);
                  if (error) throw error;
                  qc.invalidateQueries({ queryKey: ["enterprise_software"] });
                }}
              />
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Agregar Software</Button></DialogTrigger>
                <DialogContent className="max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Nuevo Software</DialogTitle></DialogHeader>
                  <SoftwareForm data={form} setData={setForm} businessAreas={businessAreas} aiToolCatalog={aiToolCatalog} />
                  <Button onClick={handleAdd} className="w-full mt-4" disabled={!form.name || !form.vendor || createSw.isPending}>
                    {createSw.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Agregar Software
                  </Button>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre, proveedor o departamento..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{categories.map((c: string) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
            </Select>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{depts.map((d: string) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            {(search || categoryFilter !== "all" || statusFilter !== "all" || deptFilter !== "all") && (
              <Button variant="ghost" size="icon" onClick={() => { setSearch(""); setCategoryFilter("all"); setStatusFilter("all"); setDeptFilter("all"); }}><X className="h-4 w-4" /></Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Software</TableHead><TableHead>Categoría</TableHead><TableHead>Proveedor</TableHead>
                <TableHead>Versión</TableHead><TableHead>Departamento</TableHead><TableHead className="text-center">Usuarios</TableHead>
                <TableHead>Licencia</TableHead><TableHead className="text-right">Costo Anual</TableHead>
                <TableHead className="text-center">IA Integrada</TableHead><TableHead className="text-center">Fuentes</TableHead>
                <TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">{enterpriseSw.length === 0 ? 'No hay software registrado' : 'No se encontraron registros'}</TableCell></TableRow>
              ) : filtered.map((sw: any) => (
                <TableRow key={sw.id}>
                  <TableCell className="font-medium">{sw.name}</TableCell>
                  <TableCell><Badge variant="outline">{sw.category}</Badge></TableCell>
                  <TableCell>{sw.vendor}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{sw.version}</TableCell>
                  <TableCell>{sw.department}</TableCell>
                  <TableCell className="text-center">{sw.users_count || 0}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{licenseTypeLabels[sw.license_type] || sw.license_type}</Badge></TableCell>
                  <TableCell className="text-right font-medium">${(sw.annual_cost || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-center">
                    {sw.integration_with_ai ? (
                      <div className="flex flex-col items-center gap-1">
                        <Badge className="bg-primary/10 text-primary text-xs">Sí</Badge>
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(sw.ai_tools_connected || []).map((t: string, i: number) => <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>)}
                        </div>
                      </div>
                    ) : <Badge variant="secondary" className="text-xs">No</Badge>}
                  </TableCell>
                  <TableCell className="text-center">
                    {(() => {
                      const count = dataSources.filter((ds: any) => ds.linked_software_id === sw.id).length;
                      return count > 0 ? (
                        <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => setLinkedSourcesModal({ swName: sw.name, swId: sw.id })}>
                          <Database className="h-3 w-3" />{count}
                        </Button>
                      ) : <span className="text-muted-foreground text-xs">0</span>;
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge className={sw.status === "active" ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}>
                      {sw.status === "active" ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => setEditing({ ...sw })}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => onDeleteConfirm({ type: 'software', id: sw.id, name: sw.name })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>{filtered.length} de {enterpriseSw.length} registros</span>
            <div className="flex items-center gap-4">
              <span>Costo total anual: <strong className="text-foreground">${enterpriseSw.reduce((s: number, sw: any) => s + (sw.annual_cost || 0), 0).toLocaleString()}</strong></span>
              <span>Con IA: <strong className="text-primary">{enterpriseSw.filter((s: any) => s.integration_with_ai).length}/{enterpriseSw.length}</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Software Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Software</DialogTitle></DialogHeader>
          {editing && (
            <>
              <SoftwareForm data={editing} setData={setEditing} businessAreas={businessAreas} aiToolCatalog={aiToolCatalog} />
              <Button onClick={handleEdit} className="w-full mt-4" disabled={updateSw.isPending}>
                {updateSw.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar Cambios
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Linked Data Sources Modal */}
      {linkedSourcesModal && (
        <Dialog open={!!linkedSourcesModal} onOpenChange={(o) => !o && setLinkedSourcesModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Fuentes de datos de "{linkedSourcesModal.swName}"</DialogTitle></DialogHeader>
            {(() => {
              const linked = dataSources.filter((ds: any) => ds.linked_software_id === linkedSourcesModal.swId);
              return linked.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-6">No hay fuentes de datos vinculadas.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Nombre</TableHead><TableHead>Tipo</TableHead><TableHead>Tecnología</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {linked.map((ds: any, i: number) => (
                      <TableRow key={ds.id}>
                        <TableCell className="font-medium text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{ds.name}</TableCell>
                        <TableCell><Badge variant="secondary">{getDataSourceTypeLabel(ds.type)}</Badge></TableCell>
                        <TableCell className="text-muted-foreground">{ds.technology || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
